import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, PageHeader, Field, Button } from "@/components/ui-kit";
import { TrendingUp, Truck, Package, User } from "lucide-react";
import { formatNumber, todayISO } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";

const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

export default function BatchingMaterialRekap() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState({ by_sopir: [], by_material: [], total_trips: 0, total_volume_m3: 0, total_qty_kg: 0, unique_trucks_count: 0, sjs: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/batching/rekap-material", { params: { date_from: from, date_to: to } });
      setData(r.data || {});
    } catch { setData({ by_sopir: [], by_material: [], total_trips: 0, total_volume_m3: 0, total_qty_kg: 0, unique_trucks_count: 0, sjs: [] }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1500px] mx-auto space-y-6">
        <PageHeader overline="Batching Plant · Rekap" title="Rekap Pengiriman Material" description="Agregasi pengiriman material batching plant (batu, pasir, dsb) per sopir + per jenis material." icon={TrendingUp} />
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <Field label="Dari Tanggal"><DatePicker value={from} onChange={(e) => setFrom(e.target.value)} data-testid="material-rekap-from" /></Field>
            <Field label="Sampai Tanggal"><DatePicker value={to} onChange={(e) => setTo(e.target.value)} data-testid="material-rekap-to" /></Field>
            <Button onClick={load} disabled={loading} data-testid="material-rekap-apply">{loading ? "Memuat..." : "Terapkan Filter"}</Button>
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-5"><div className="flex items-center gap-3"><Truck className="h-7 w-7 text-terra-600" /><div><div className="text-[10px] uppercase tracking-widest text-stone-500">Total Pengiriman</div><div className="text-2xl font-bold text-stone-900 font-mono" data-testid="material-rekap-total-trips">{formatNumber(data.total_trips || 0)}</div></div></div></Card>
          <Card className="p-5"><div className="flex items-center gap-3"><TrendingUp className="h-7 w-7 text-blue-600" /><div><div className="text-[10px] uppercase tracking-widest text-stone-500">Volume (m³)</div><div className="text-2xl font-bold text-stone-900 font-mono" data-testid="material-rekap-total-volume">{formatNumber(data.total_volume_m3 || 0, 3)}</div></div></div></Card>
          <Card className="p-5"><div className="flex items-center gap-3"><Package className="h-7 w-7 text-amber-600" /><div><div className="text-[10px] uppercase tracking-widest text-stone-500">Berat (kg)</div><div className="text-2xl font-bold text-stone-900 font-mono" data-testid="material-rekap-total-kg">{formatNumber(data.total_qty_kg || 0, 3)}</div></div></div></Card>
          <Card className="p-5"><div className="flex items-center gap-3"><User className="h-7 w-7 text-stone-600" /><div><div className="text-[10px] uppercase tracking-widest text-stone-500">Truk Berbeda</div><div className="text-2xl font-bold text-stone-900 font-mono">{formatNumber(data.unique_trucks_count || 0)}</div></div></div></Card>
        </div>

        <Card>
          <div className="px-4 py-3 border-b border-stone-200 font-semibold text-sm">Rekap per Sopir</div>
          <table className="w-full text-sm" data-testid="material-rekap-sopir-table">
            <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
              <tr><th className="text-left py-2 px-3">Sopir</th><th className="text-right py-2 px-3">Jumlah Pengiriman</th><th className="text-right py-2 px-3">Volume (m³)</th><th className="text-right py-2 px-3">Berat (kg)</th><th className="text-left py-2 px-3">Truk Dipakai</th></tr>
            </thead>
            <tbody>
              {(data.by_sopir || []).length === 0 && <tr><td colSpan={5} className="text-center py-8 text-stone-400 text-xs">Tidak ada data</td></tr>}
              {(data.by_sopir || []).map((r, i) => (
                <tr key={i} className="border-b border-stone-100">
                  <td className="py-2 px-3 font-medium">{r.sopir}</td>
                  <td className="py-2 px-3 text-right font-mono">{formatNumber(r.trips)}</td>
                  <td className="py-2 px-3 text-right font-mono">{r.volume_m3 > 0 ? formatNumber(r.volume_m3, 3) : "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-amber-700">{r.qty_kg > 0 ? formatNumber(r.qty_kg, 3) : "—"}</td>
                  <td className="py-2 px-3 text-[11px] text-stone-500">{(r.trucks || []).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card>
          <div className="px-4 py-3 border-b border-stone-200 font-semibold text-sm">Rekap per Jenis Material</div>
          <table className="w-full text-sm" data-testid="material-rekap-material-table">
            <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
              <tr><th className="text-left py-2 px-3">Material</th><th className="text-right py-2 px-3">Item Pengiriman</th><th className="text-right py-2 px-3">Volume (m³)</th><th className="text-right py-2 px-3">Berat (kg)</th></tr>
            </thead>
            <tbody>
              {(data.by_material || []).length === 0 && <tr><td colSpan={4} className="text-center py-8 text-stone-400 text-xs">Tidak ada data</td></tr>}
              {(data.by_material || []).map((r, i) => (
                <tr key={i} className="border-b border-stone-100">
                  <td className="py-2 px-3 font-medium">{r.material}</td>
                  <td className="py-2 px-3 text-right font-mono">{formatNumber(r.trips)}</td>
                  <td className="py-2 px-3 text-right font-mono text-blue-700">{r.volume_m3 > 0 ? formatNumber(r.volume_m3, 3) : "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-amber-700">{r.qty_kg > 0 ? formatNumber(r.qty_kg, 3) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
